import React from 'react';

// The rendering of `scoring_state` from the two ranking endpoints (Candidate
// Graph, Targeted Outreach): `ready` | `computing` | `failed`.
//
// `ready` renders nothing -- the page underneath is the answer, and a banner
// that stays around after the thing it announced is done is noise.
//
// `computing` is deliberately INLINE, not a full-page takeover: the nav,
// filters and header must stay usable while a first-time tenant's score
// finishes in the background. And it carries an elapsed COUNT, never a
// percentage -- the scorers report counts, not fractions, and a bar that
// jumps from 0 to 100 the moment the run lands is a worse answer than an
// honest clock.
//
// `failed` hands the recruiter a way forward rather than a dead end.

function formatElapsed(seconds) {
  const total = Math.max(0, Math.floor(seconds || 0));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return minutes > 0 ? `${minutes}:${String(rest).padStart(2, '0')}` : `${rest}s`;
}

export default function ScoringBanner({ state, message, elapsedSeconds = 0, onRetry }) {
  if (state === 'ready') return null;

  if (state === 'failed') {
    return (
      <div
        data-testid="scoring-banner"
        role="alert"
        className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3 mb-6 text-sm flex flex-wrap items-center justify-between gap-3"
      >
        <span>{message || 'Scoring ran into a problem.'}</span>
        <button
          type="button"
          data-testid="scoring-retry"
          onClick={onRetry}
          className="shrink-0 px-3 py-1.5 rounded-lg text-sm font-medium border border-red-300 text-red-800 hover:bg-red-100"
        >
          Try again
        </button>
      </div>
    );
  }

  // state === 'computing'
  return (
    <div
      data-testid="scoring-banner"
      role="status"
      className="bg-sky-50 border border-sky-200 text-sky-900 rounded-lg px-4 py-3 mb-6 text-sm flex items-center gap-3"
    >
      <span
        aria-hidden="true"
        className="inline-block w-2 h-2 rounded-full bg-sky-500 animate-pulse shrink-0"
      />
      <span>
        Calculating scores for the first time. This usually takes about a minute.{' '}
        <span data-testid="scoring-elapsed" className="font-medium tabular-nums">
          {formatElapsed(elapsedSeconds)} elapsed
        </span>
      </span>
    </div>
  );
}
